import React from 'react';
import { HelpHint, panelClass } from '../governed-mcp-gateway/components/GatewayUi';
import { coercePolicies, defaultPolicyFlag } from './omniChatConstants';
import type { OmniChatPolicies, OmniChatPolicyFlag, OmniChatPolicyId, OmniChatPolicyMode } from './omniChatTypes';

const SAFETY: { id: OmniChatPolicyId; title: string; help: string }[] = [
  {
    id: 'grounding',
    title: 'Grounding',
    help: 'Answer from this agent’s instruction and gateway. Enforce refuses when no source supports the answer.',
  },
  {
    id: 'promptInjection',
    title: 'Prompt injection',
    help: 'Treat user text and retrieved documents as untrusted. Ignore attempts to override tools or instructions.',
  },
  {
    id: 'pii',
    title: 'PII',
    help: 'Scan prompts and completions for personal data. Enforce redacts matches instead of only logging them.',
  },
  {
    id: 'prohibitedContent',
    title: 'Prohibited content',
    help: 'Block hate, sexual, self-harm, weapons, and similar content.',
  },
];

const BEHAVIOR: { id: OmniChatPolicyId; title: string; help: string }[] = [
  {
    id: 'topicFence',
    title: 'Topic fence',
    help: 'Keep replies inside this agent’s instruction.',
  },
  {
    id: 'confirmWrites',
    title: 'Confirm writes',
    help: 'Ask before mutate tools such as send, update, or refund. Read tools stay free.',
  },
];

function Switch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-5 w-9 rounded-full transition-colors disabled:opacity-40 ${
        checked ? 'bg-indigo-600' : 'bg-neutral-300'
      }`}
    >
      <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : ''}`} />
    </button>
  );
}

function ModeToggle({
  value,
  disabled,
  isLight,
  onChange,
}: {
  value: OmniChatPolicyMode;
  disabled?: boolean;
  isLight: boolean;
  onChange: (next: OmniChatPolicyMode) => void;
}) {
  return (
    <div className={`inline-flex rounded-lg p-0.5 text-[11px] font-medium ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
      {(['monitor', 'enforce'] as OmniChatPolicyMode[]).map((mode) => (
        <button
          key={mode}
          type="button"
          disabled={disabled}
          onClick={() => onChange(mode)}
          className={`px-2.5 py-1 rounded-md capitalize disabled:opacity-50 ${
            value === mode
              ? isLight
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'bg-neutral-900 text-indigo-300'
              : isLight
                ? 'text-neutral-500'
                : 'text-neutral-400'
          }`}
        >
          {mode}
        </button>
      ))}
    </div>
  );
}

function PolicyRow({
  title,
  help,
  flag,
  editing,
  isLight,
  onChange,
}: {
  title: string;
  help: string;
  flag: OmniChatPolicyFlag;
  editing: boolean;
  isLight: boolean;
  onChange: (next: OmniChatPolicyFlag) => void;
}) {
  return (
    <div className={`rounded-xl border p-3.5 ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className={`text-sm font-medium ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>{title}</p>
          <HelpHint text={help} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {flag.enabled ? (
            editing ? (
              <ModeToggle
                value={flag.mode}
                isLight={isLight}
                onChange={(mode) => onChange({ ...flag, mode })}
              />
            ) : (
              <span
                className={`rounded-md px-2 py-0.5 text-[11px] font-medium capitalize ${
                  isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
                }`}
              >
                {flag.mode}
              </span>
            )
          ) : null}
          <Switch checked={flag.enabled} disabled={!editing} onChange={(enabled) => onChange({ ...flag, enabled })} />
        </div>
      </div>
    </div>
  );
}

function PolicyGroup({
  title,
  hint,
  ids,
  policies,
  editing,
  isLight,
  onChange,
}: {
  title: string;
  hint: string;
  ids: { id: OmniChatPolicyId; title: string; help: string }[];
  policies: OmniChatPolicies;
  editing: boolean;
  isLight: boolean;
  onChange: (next: OmniChatPolicies) => void;
}) {
  return (
    <section className={`${panelClass(isLight)} p-5 space-y-3`}>
      <div>
        <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{title}</h3>
        <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{hint}</p>
      </div>
      {ids.map((item) => (
        <PolicyRow
          key={item.id}
          title={item.title}
          help={item.help}
          flag={policies[item.id] ?? defaultPolicyFlag()}
          editing={editing}
          isLight={isLight}
          onChange={(flag) => onChange({ ...policies, [item.id]: flag })}
        />
      ))}
    </section>
  );
}

export default function OmniChatAdvancedTab({
  policies,
  editing,
  isLight,
  onChange,
}: {
  policies?: OmniChatPolicies | null;
  editing: boolean;
  isLight: boolean;
  onChange: (next: OmniChatPolicies) => void;
}) {
  const safe = coercePolicies(policies);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <PolicyGroup
        title="Safety"
        hint="What this agent may read and say."
        ids={SAFETY}
        policies={safe}
        editing={editing}
        isLight={isLight}
        onChange={onChange}
      />
      <PolicyGroup
        title="Behavior"
        hint="How this agent stays on task and asks before writes."
        ids={BEHAVIOR}
        policies={safe}
        editing={editing}
        isLight={isLight}
        onChange={onChange}
      />
    </div>
  );
}
